/** @type {import('tailwindcss').Config} */

import daisyui from 'daisyui';
import typography from '@tailwindcss/typography';
import scrollbar from 'tailwind-scrollbar';
import { light as lightTheme, dark as darkTheme } from 'daisyui/src/theming/themes';

export const primaryColor = '#F97F2A'; // Orange for branding (main brand color)
export const secondaryColor = '#F97F2A'; // Same as primary
export const accentColor = '#2B3440'; // Dark blue for buttons, cards
export const errorColor = 'oklch(0.577 0.245 27.325)';
export const linkColor = '65.8% 0.169 249';

// Semantic color system - use these in components instead of hardcoded values
export const colors = {
  // Brand colors (orange shades)
  brand: {
    primary: primaryColor, // Main brand orange - references exported constant
    hover: '#e8701f', // Darker orange for hover
    'hover-alt': '#e06d1f', // Alternative darker orange
    active: '#f97316', // Orange for active states
    light: '#ff6b35', // Lighter reddish orange
    dark: '#e55a2b', // Darker reddish orange
  },
  // Dark theme colors (navy/blue shades)
  dark: {
    primary: '#1a2332', // Darkest navy (admin sidebar)
    secondary: accentColor, // Dark blue (buttons, cards) - references exported constant
    hover: '#2a3442', // Hover state for dark primary
    'hover-secondary': '#3d4559', // Hover state for dark secondary
  },
  // Neutral colors
  neutral: {
    'bg-light': '#f5f5f5', // Light gray background
    'border-gray': '#CCCCCC', // Gray borders
    highlight: '#ffe0b2', // Light orange/beige highlight
  },
  // Status colors
  status: {
    error: '#EF4444', // Red for errors
  },
};

export default {
  content: [
    './index.html',
    './src/**/*.{js,ts,jsx,tsx}',
    'node_modules/daisyui/dist/**/*.js',
    'node_modules/react-daisyui/dist/**/*.js',
  ],
  theme: {
    extend: {
      colors: {
        link: 'oklch(var(--link) / <alpha-value>)',
        'text-secondary': 'rgba(var(--text-secondary))',
        // Add semantic colors
        brand: colors.brand,
        dark: colors.dark,
        neutral: colors.neutral,
        status: colors.status,
      },
      fontFamily: {
        sans: ['"Inter Variable"', 'sans-serif'],
        varela: ['"Varela Round"', 'sans-serif'],
        lato: ['Lato', 'sans-serif'],
      },
      fontSize: {
        '4.5xl': [
          '2.5rem',
          {
            lineHeight: '2.75rem',
          },
        ],
      },
      boxShadow: {
        full: '0px 0px 20px 20px',
      },
      typography: {
        DEFAULT: {
          css: {
            color: 'inherit',
            'h1, h2, h3, h4, h5, h6, a, span, strong, code': {
              color: 'inherit',
            },
            li: {
              '&::marker': {
                color: 'inherit',
              },
            },
          },
        },
      },
    },
  },
  plugins: [typography, scrollbar({ nocompatible: true }), daisyui],
  important: '#root',
  daisyui: {
    themes: [
      {
        dark: {
          ...darkTheme,
          primary: primaryColor,
          'primary-content': '#ffffff',
          secondary: secondaryColor,
          'secondary-content': '#ffffff',
          accent: accentColor,
          'accent-content': '#ffffff',
          error: errorColor,
          'error-content': '#ffffff',
          '--link': linkColor,
          '--text-secondary': '255, 255, 255, 0.6',
          // Admin panel colors (can be overridden for dark mode)
          '--admin-bg': colors.dark.primary,
          '--admin-hover': colors.dark.hover,
          '--admin-active': colors.brand.active,
        },
      },
      {
        light: {
          ...lightTheme,
          primary: primaryColor,
          'primary-content': '#ffffff',
          secondary: secondaryColor,
          'secondary-content': '#ffffff',
          accent: accentColor,
          'accent-content': '#ffffff',
          error: errorColor,
          'error-content': '#ffffff',
          '--link': linkColor,
          '--text-secondary': '0, 0, 0, 0.6',
          // Admin panel colors (can be overridden for dark mode)
          '--admin-bg': colors.dark.primary,
          '--admin-hover': colors.dark.hover,
          '--admin-active': colors.brand.active,
        },
      },
    ],
  },
};
